<template>
  <!-- Default joined bottom bar: plan_map, explore_v2 -->
  <transition name="trail-card-slide">
    <div class="trail-data-card" :class="{ reduce: isReduced }" v-if="hasData && !floatingLayout">

      <div v-if="clusterNavActive" class="cluster-nav">
        <button class="cluster-nav-btn" @click="navigateClusterPrev">
          <span class="material-icons">chevron_left</span>
        </button>
        <span class="cluster-nav-label">{{ $t('trail_data_card.cluster_current_index_of_cluster_total', { cluster_current_index: clusterCurrentIndex + 1, cluster_total_count: clusterTotalCount }) }}</span>
        <button class="cluster-nav-btn" @click="navigateClusterNext">
          <span class="material-icons">chevron_right</span>
        </button>
      </div>

      <TrailDataCardWeather
        v-if="showWeather"
        :showWeather="showWeather"
        :weather="weather?.start"
        :shortAddress="short_address"
        @hide-weather="hideWeather"
      />

      <div class="card-controls">
        <span class="close-button material-icons" @click="closeCard" :title="$t('trail_data_card.close_card')">
          close
        </span>
        <span class="hide-show material-icons" @click="toggleReduce" :title="isReduced ? $t('trail_data_card.show_details') : $t('trail_data_card.hide_details')">
          {{ isReduced ? 'add' : 'remove' }}
        </span>
      </div>
      <div class="left-panel align-content-center">
        <a v-if="shareableUrl" :href="shareableUrl" class="d-flex align-self-start thumbnail-container h-100">
          <img class="object-fit-cover" :src="mainImageUrl" alt="" />
        </a>

        <div class="px-2 w-100 d-flex flex-column justify-content-center p-2">
          <p class="d-flex flex-row flex-wrap mb-2">
            <button
              v-if="weather && weather.start"
              @click.stop="toggleWeather()"
              class="badge badge-dark d-flex align-items-center m-0 mr-1" style="cursor: pointer;"
              ref="weatherTooltipButton"
              type="button"
            >
              <span
                ref="weatherTooltip"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                data-bs-html="true"
                :title="weatherTooltipContent"
                style="pointer-events: auto;"
                class="d-flex align-items-center"
              >
                <small class="pr-0">
                  <img :src="`https://openweathermap.org/img/wn/${weather?.start?.current?.icon}@2x.png`"
                                         width="20" height="20" alt="" />
                </small>
                {{ Math.round(weather?.start?.daily?.temperature_2m_max?.[0]) }}°C
              </span>
              <span class="material-icons ml-1" style="font-size: 1.2em;"><strong>add</strong></span>
            </button>

            <span
              v-if="averageRating"
              class="badge badge-dark d-flex align-items-center m-0 mr-1" >
              <small>
                <span class="material-icons mr-1">star</span>
              </small>
              {{ averageRating }}
            </span>

            <span class="dropdown">
              <span
                class="badge badge-dark d-flex align-items-center m-0 mr-1" type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style="height: 100% !important; cursor: pointer;"
              >
                <span
                  ref="optionsButtonTooltip"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  data-bs-html="true"
                  :title="$t('trail_data_card.see_options_to_modify_this_trail')"
                >
                  <small>
                    <span class="material-icons" style="font-size: 17px;">more_vert</span>
                  </small>
                </span>
              </span>
              <ul class="dropdown-menu">
                <li class="dropdown-item" @click="onGpxClick">
                  {{ $t('trail_data_card.export_gpx') }}
                </li>
                <li v-if="showEditInPlanner" class="dropdown-item" @click="editInPlanner">
                  {{ editInPlannerText }}
                </li>
                <li v-if="isDetailsEditable" class="dropdown-item" @click="editDetails">
                  {{ $t('trail_data_card.edit_details') }}
                </li>
                <li class="dropdown-item" @click="onShareClick">
                  {{ $t('shared.share') }}
                </li>
                <li class="dropdown-item" @click="preview">
                  {{ $t('shared.3d_flyover') }}
                </li>
                <li v-if="isCropable" class="dropdown-item" @click="crop">
                  {{ $t('trail_data_card.crop_recording') }}
                </li>
                <li v-if="isGarminConnected()" class="dropdown-item" @click="sendToGarmin">
                  {{ $t('shared.send_to_garmin') }}
                </li>
                <li v-if="canReportRecordingIssue" class="dropdown-item" @click="reportRecordingIssue">
                  {{ $t('trail_data_card.report_as_inaccurate') }}
                </li>
                <li v-if="isDeletable" class="dropdown-item" @click="onDeleteClick">
                  {{ $t('shared.delete') }}
                </li>
              </ul>
            </span>

            <a v-if="shareableUrl" :href="shareableUrl" class="badge badge-dark d-flex align-items-center m-0 mr-1" :title="$t('trail_data_card.open_trail_page')"> <small><span class="material-icons" style="font-size: 17px;">open_in_new</span></small>
            </a>
          </p>

          <a :href="shareableUrl || '#'" :target="shareableUrl ? '_blank' : '_self'"><strong>{{ name }}</strong></a> <p v-if="short_address" class="mb-1">{{ short_address }}</p>
          <p v-if="difficulty" class="text-muted"><small>{{ difficulty }}</small></p> <p>
          <small>
            {{ formatDistance(distanceMeters) }}
            <span class="mx-1">&#183;</span>
            {{ formatElevation(elevationGainMeters) }}
            <span class="mx-1">&#183;</span>
            ~{{ duration() }}
          </small>
        </p>
        </div>
      </div>

      <div class="right-panel p-2 position-relative">
        <ElevationProfile
          :coordinates="chartCoordinates"
          :highlights="projectedHighlights"
          :get-map="getMapForMarker"
          :collapsible="false"
          :expandable="false"
          :show-stats="false"
          :max-points="120"
        />
      </div>
    </div>
  </transition>

  <!-- Floating layout: recording map. A left-docked sidebar (consistent with
       the hike map's trail panel) plus an independent elevation bar, centered
       in the remaining map area. -->
  <div
    v-if="hasData && floatingLayout"
    class="recording-sidebar"
    :class="{ 'mobile-open': mobileDetailsOpen, 'above-elevation-panel-v2': elevationPanelV2Enabled }"
  >
    <button
      v-if="isReduced"
      class="sidebar-tab"
      type="button"
      :title="$t('trail_data_card.show_trail_details')"
      @click="toggleReduce"
    >
      <span class="material-icons">chevron_right</span>
    </button>

    <div v-else class="sidebar-panel">
      <div class="sidebar-header">
        <button class="sidebar-icon-btn" type="button" :title="$t('hike_map.elevation_profile.minimize')" @click="sidebarBackClick">
          <span class="material-icons">chevron_left</span>
        </button>
        <span class="sidebar-title">{{ name }}</span>
        <span class="dropdown sidebar-more">
          <button
            class="sidebar-icon-btn" type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            :title="$t('trail_data_card.more_options')"
          >
            <span class="material-icons">more_vert</span>
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li class="dropdown-item" @click="onShareClick">
              {{ $t('shared.share') }}
            </li>
            <li v-if="shareableUrl">
              <a class="dropdown-item" :href="shareableUrl" target="_blank">{{ $t('trail_data_card.open_trail_page') }}</a>
            </li>
            <li class="dropdown-item" @click="onGpxClick">
              {{ $t('trail_data_card.export_gpx') }}
            </li>
            <li v-if="showEditInPlanner" class="dropdown-item" @click="editInPlanner">
              {{ editInPlannerText }}
            </li>
            <li v-if="isDetailsEditable" class="dropdown-item" @click="editDetails">
              {{ $t('trail_data_card.edit_details') }}
            </li>
            <li class="dropdown-item" @click="preview">
              {{ $t('shared.3d_flyover') }}
            </li>
            <li v-if="isCropable" class="dropdown-item" @click="crop">
              {{ $t('trail_data_card.crop_recording') }}
            </li>
            <li v-if="isGarminConnected()" class="dropdown-item" @click="sendToGarmin">
              {{ $t('shared.send_to_garmin') }}
            </li>
            <li v-if="canReportRecordingIssue" class="dropdown-item" @click="reportRecordingIssue">
              {{ $t('trail_data_card.report_as_inaccurate') }}
            </li>
            <li v-if="isDeletable" class="dropdown-item" @click="onDeleteClick">
              {{ $t('shared.delete') }}
            </li>
            <li class="dropdown-item" @click="closeCard">
              {{ $t('shared.close') }}
            </li>
          </ul>
        </span>
      </div>

      <div class="sidebar-body">
        <p v-if="short_address" class="sidebar-address">{{ short_address }}</p>
        <button
          v-if="weather && weather.start"
          class="sidebar-weather-chip"
          type="button"
          :title="$t('trail_data_card.view_weather')"
          @click.stop="toggleWeather"
        >
          <img :src="`https://openweathermap.org/img/wn/${weather?.start?.current?.icon}@2x.png`" alt="" />
          {{ Math.round(weather?.start?.daily?.temperature_2m_max?.[0]) }}°C
        </button>
        <span v-if="averageRating" class="sidebar-rating" :title="$t('trail_data_card.average_rating')">
          <span class="material-icons">star</span>{{ averageRating }}
        </span>
      </div>

      <div class="sidebar-footer">
        <span class="sidebar-footer-label">{{ $t('hike_map.hike_trails_panel.total') }}</span>
        <span class="sidebar-footer-value">{{ formatDistance(distanceMeters) }}</span>
        <span class="sidebar-footer-value">{{ formatElevation(elevationGainMeters) }}</span>
      </div>

      <TrailDataCardWeather
        v-if="showWeather"
        :showWeather="showWeather"
        :weather="weather?.start"
        :shortAddress="short_address"
        @hide-weather="hideWeather"
      />
    </div>
  </div>

  <!-- elevation-panel-v2 (feature-flagged pilot): a self-contained
       replacement with its own bottom-sheet chrome (peek/default/expanded on
       mobile, a floating panel on desktop) — it does not use the legacy
       mobileElevationOpen toggle or mobile-sheet-close button below, both of
       which stay wired to the fallback ElevationProfile only. -->
  <ElevationPanelV2
    v-if="hasData && floatingLayout && elevationPanelV2Enabled"
    :mode="type === 'Recording' ? 'recording' : 'planned'"
    :coordinates="chartCoordinates"
    :get-map="getMapForMarker"
    :stats="{ distanceMeters, gainMeters: elevationGainMeters, lossMeters: null, durationSeconds }"
    :max-points="120"
    :initial-snap="initialElevationSnap"
    class="floating-elevation-bar floating-elevation-bar--v2"
    @snap-change="onElevationPanelV2SnapChange"
  />

  <div
    v-else-if="hasData && floatingLayout"
    class="floating-elevation-bar"
    :class="{ 'mobile-open': mobileElevationOpen }"
  >
    <button class="mobile-sheet-close" type="button" :title="$t('shared.close')" @click="mobileElevationOpen = false">
      <span class="material-icons">close</span>
    </button>
    <ElevationProfile
      :coordinates="chartCoordinates"
      :highlights="projectedHighlights"
      :get-map="getMapForMarker"
      collapsible
      :expandable="!mobileElevationOpen"
      show-stats
      :title="$t('shared.elevation')"
      :stats="{ distanceMeters, gainMeters: elevationGainMeters, lossMeters: null, durationSeconds }"
      :max-points="120"
    />
  </div>

  <!-- Mobile-only tab bar: opens the sidebar/elevation bar above as bottom
       sheets instead of showing both permanently on a cramped screen. The
       "Elevation" tab is dropped when elevation-panel-v2 is active — its own
       bottom sheet is always visible (peek state), so there's nothing for a
       toggle button to reveal. -->
  <div
    v-if="hasData && floatingLayout"
    class="recording-mobile-tabs"
    :class="{
      hidden:
        mobileDetailsOpen ||
        (mobileElevationOpen && !elevationPanelV2Enabled) ||
        (elevationPanelV2Enabled && elevationPanelV2SnapKey !== 'peek'),
      'above-elevation-panel-v2': elevationPanelV2Enabled && elevationPanelV2SnapKey === 'peek',
    }"
  >
    <button class="mobile-tab-btn" type="button" @click="toggleMobileDetails">
      <span class="material-icons">info</span> {{ $t('hike_map.details') }}
    </button>
    <template v-if="!elevationPanelV2Enabled">
      <span class="mobile-tab-divider"></span>
      <button class="mobile-tab-btn" type="button" @click="toggleMobileElevation">
        <span class="material-icons">show_chart</span> {{ $t('shared.elevation') }}
      </button>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import mapboxgl from 'mapbox-gl';
import ElevationProfile from '../components/ElevationProfile.vue';
import ElevationPanelV2 from '../elevation_panel_v2/ElevationPanelV2.vue';
import { projectHighlightsOntoLine, type ProjectedHighlight } from '../../utils/elevation/projectHighlights';
import { buildHikeHighlightInputs, type RawHikeHighlight } from './highlightInputs';
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref';
import { PrettyTime } from '../../utils/helpers/HikeTime';
import getWeatherIcon from '../../utils/getWeatherIcon';
import getWeatherDescription from "../../utils/getWeatherDescription";
import TrailDataCardWeather from './TrailDataCardWeather.vue';
import { geojsonToGpx } from "../../utils/geojsonToGpx";
import graph_client from "../../utils/graphql/graph_client";
import { DestroyPtv2PlannedTrail } from "../../utils/graphql/planned-trail-graph";
import { RevenueCatWrapper } from "../../utils/RevenueCatWrapper";
import { HiikerMapStyle } from "../../utils/models/MapStyles";
import { DestroyRecording } from "../../utils/graphql/recorded-trail-graph";
import { Tooltip } from 'bootstrap';
import {SEND_TO_GARMIN} from "../../utils/graphql/send-to-garmin";
import { timestampToIso } from "../../utils/timestampToIso";
import {getFeatureCollectionLineString} from "../../utils/getFeatureCollectionLineString";

export default defineComponent({
  name: 'TrailDataCard',
  props: {
    reduced: {
      type: Boolean,
      default: false,
    },
    // When true, renders a small minimisable data card in the top-left corner
    // plus an independent elevation bar at the bottom, instead of the default
    // single joined bottom bar. Used by the recording map page; plan_map and
    // explore_v2 keep the default joined layout.
    floatingLayout: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    TrailDataCardWeather,
    ElevationProfile,
    ElevationPanelV2,
  },
  data() {
    return {
      // UI
      isReduced: this.reduced,
      isLoading: false,
      isDetailsEditable: false,
      actionMenuOpen: false,

      // Trail info
      id: '',
      obfuscatedSlug: '', // Store obfuscatedSlug for recordings and hikes
      ownerFirebaseUserId: null as string | null, // Store owner's firebase user ID
      type: '',
      name: '',
      short_address: '',
      difficulty: '',
      averageRating: '',
      elevationGainMeters: 0,
      distanceMeters: 0,
      durationSeconds: 0,
      isDeletable: false,
      isCropable: false,

      // Geometry & images
      featureCollection: null as any,
      mainImageUrl: '',
      createdAt: '',
      shareableUrl: '',
      startPoint: null as number[] | null,
      endPoint: null as number[] | null,

      timestamps: null as string[] | undefined,

      // Hike highlights (waypoints/POIs projected onto the elevation profile)
      hikeHighlights: [] as RawHikeHighlight[],

      // Cluster navigation
      clusterNavActive: false,
      clusterCurrentIndex: 0,
      clusterTotalCount: 0,

      // Weather
      weather: null as any,
      showWeather: false,
      weatherTooltip: null as Tooltip | null, // Typed Bootstrap Tooltip
      optionsTooltip: null as Tooltip | null, // Typed Bootstrap Tooltip
      editUrl: null as string | null, // Typed editUrl

      // Floating layout (recording map) mobile bottom-sheet state: on narrow
      // screens the sidebar and elevation bar are hidden behind a tab bar
      // instead of both competing for space, matching the hike map's
      // Routes/Elevation mobile pattern.
      mobileDetailsOpen: false,
      mobileElevationOpen: false,
      // elevation-panel-v2's current bottom-sheet snap key ('peek' | 'default'
      // | 'expanded'), reported via its `snap-change` emit. Used to hide the
      // mobile tab bar's "Details" pill whenever the sheet isn't collapsed to
      // peek — both share the same fixed bottom-of-viewport stacking context,
      // so without this the pill renders on top of the sheet's own content.
      elevationPanelV2SnapKey: 'peek',
    };
  },
  watch: {
    reduced(newVal) {
      this.isReduced = newVal;
    },
    // --- WATCHER TO REDRAW CHART ON DATA CHANGE ---
    featureCollection: {
      // immediate: true, // Removed immediate, rely on mounted and manual calls
      deep: true, // Add deep watch if featureCollection structure might change internally
      handler() {
        // The elevation profile (ElevationProfile) rebuilds itself reactively
        // from the `chartCoordinates` prop, so no manual redraw is needed here.
        this.setStartPointAndEndPointFromFeatureCollection(); // Recalculate points if data changes
        this.getWeather(); // Fetch new weather if points change
      },
    },
  },
  computed: {
    hasData() {
      return !!this.featureCollection && !!this.featureCollection.features?.length;
    },
    // Staged rollout gate for the elevation-panel-v2 redesign (recording_map
    // pilot). See AppSetting.elevation_panel_v2_enabled? / window.elevationPanelV2Enabled.
    elevationPanelV2Enabled() {
      return !!(window as any).elevationPanelV2Enabled;
    },
    // On mobile this panel mounts unconditionally alongside the map (unlike
    // hike map, where it only mounts once the user taps the "Elevation" tab),
    // so it must start collapsed to 'peek' — otherwise the chart covers the
    // screen on load instead of resting behind the mobile tab bar like the
    // hike map's compact "Details | Elevation" pill. Desktop keeps the chart
    // open by default, matching the hike map's always-visible desktop panel.
    initialElevationSnap(): 'peek' | 'default' {
      return window.matchMedia('(max-width: 768px)').matches ? 'peek' : 'default';
    },
    currentWeatherDescription() {
      if (!this.weather?.start?.daily) return '';
      return getWeatherDescription(this.weather.start.daily.weather_code[0]);
    },
    weatherTooltipContent() {
      return `<span class="text-center d-flex flex-column"><strong>${this.currentWeatherDescription}</strong><small style='opacity: 0.5;'>${this.$t('trail_data_card.click_for_more')}</small></span>`;
    },
    showEditInPlanner() {
      // Always show edit option for hikes on explore map
      return this.type === 'Hike';
    },
    editInPlannerText() {
      return this.$t('hikes.show.edit_a_copy');
    },
    canReportRecordingIssue() {
      return this.type === 'Recording' && this.isCurrentUserAdmin();
    },

    // Flattened `[lon, lat, (ele)]` line for the shared ElevationProfile, which
    // builds its own Chart.js data (equal-distance downsampling, gold line,
    // MeasurementPref units) from these coordinates.
    chartCoordinates(): number[][] {
      const coords = this.getFeatureCollectionLineString();
      return coords ?? [];
    },
    // Hike highlights projected onto the profile line. Empty for non-hike
    // entities (recordings / planned trails don't carry highlights here).
    projectedHighlights(): ProjectedHighlight[] {
      if (this.type !== 'Hike') return [];
      const inputs = buildHikeHighlightInputs(this.hikeHighlights);
      if (!inputs.length) return [];
      const coords = this.getFeatureCollectionLineString();
      if (!coords || coords.length < 2) return [];
      return projectHighlightsOntoLine(coords, inputs, {
        maxDistanceMeters: this.distanceMeters || undefined,
      });
    },
  },
  methods: {
    isGarminConnected() {
      if (window.user) {
        return window.user.garmin_connected || false;
      } else {
        return false
      }
    },
    getCurrentUserFirebaseId() {
      const user = (window as any).user;
      return user?.firebaseUserId || user?.firebase_user_id || null;
    },
    isOwnedByCurrentUser(ownerFirebaseUserId: string | null) {
      const currentUserFirebaseId = this.getCurrentUserFirebaseId();
      if (!ownerFirebaseUserId || !currentUserFirebaseId) {
        return false;
      }
      return ownerFirebaseUserId === currentUserFirebaseId;
    },
    isCurrentUserAdmin() {
      const user = (window as any).user;
      if (user?.admin === true || user?.admin === 'true') {
        return true;
      }

      const adminFlag = (window as any).admin;
      return adminFlag === true || adminFlag === 'true';
    },
    getRecordingReportIssueUrl() {
      if (this.obfuscatedSlug) {
        return `/recordings/users/${this.obfuscatedSlug}/report_issue`;
      }

      if (this.shareableUrl) {
        try {
          let shareablePath = new URL(this.shareableUrl, window.location.origin).pathname.replace(/\/$/, '');
          if (shareablePath.startsWith('/maps/recordings/')) {
            shareablePath = shareablePath.replace('/maps/recordings/', '/recordings/users/');
          }
          return `${shareablePath}/report_issue`;
        } catch (error) {
          console.error('Failed to parse recording shareable URL:', error);
        }
      }

      if (window.location.pathname.match(/^\/recordings\/users\/[^/]+(\/map)?$/)) {
        return window.location.pathname.replace(/\/map$/, '').replace(/\/$/, '') + '/report_issue';
      }

      return null;
    },
    navigateClusterPrev() {
      (window as any).clusterNavigate?.('prev');
    },
    navigateClusterNext() {
      (window as any).clusterNavigate?.('next');
    },
    closeCard() {
      (window as any).clearClusterNav?.();
      window.trailCardClearData?.();
      window.removeTrailLine?.();
    },
    toggleReduce() {
      this.isReduced = !this.isReduced;
      this.$emit('update:reduced', this.isReduced);
      this.$emit('toggle-reduce', this.isReduced);
    },
    // Sidebar header's back/minimize button: on the mobile bottom-sheet it
    // closes the sheet; on desktop it minimizes to the edge tab as before.
    sidebarBackClick() {
      if (this.mobileDetailsOpen) {
        this.mobileDetailsOpen = false;
      } else {
        this.toggleReduce();
      }
    },
    toggleMobileDetails() {
      this.mobileDetailsOpen = !this.mobileDetailsOpen;
      if (this.mobileDetailsOpen) this.mobileElevationOpen = false;
    },
    toggleMobileElevation() {
      this.mobileElevationOpen = !this.mobileElevationOpen;
      if (this.mobileElevationOpen) this.mobileDetailsOpen = false;
    },
    onElevationPanelV2SnapChange(key: string) {
      this.elevationPanelV2SnapKey = key;
    },
    hideWeather() {
      this.showWeather = false;
    },
    addTooltip() {
      // Dispose existing tooltips to prevent memory leaks
      if (this.weatherTooltip) {
        this.weatherTooltip.dispose();
        this.weatherTooltip = null;
      }
      if (this.optionsTooltip) {
        this.optionsTooltip.dispose();
        this.optionsTooltip = null;
      }

      // Don't initialize tooltips on small screens (mobile)
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        return; // Skip tooltip initialization on mobile
      }

      // Use nextTick to ensure refs are available after DOM updates
      this.$nextTick(() => {
        const weatherEl = this.$refs.weatherTooltip as Element | undefined;
        if (weatherEl) {
          this.weatherTooltip = new Tooltip(weatherEl, {
            trigger: "hover",
            placement: "top",
            customClass: 'weather-tooltip',
            html: true // Ensure HTML content is allowed if needed
          });
        }

        const optionsEl = this.$refs.optionsButtonTooltip as Element | undefined;
        if (optionsEl) {
          this.optionsTooltip = new Tooltip(optionsEl, {
            trigger: "hover",
            placement: "top",
            customClass: 'weather-tooltip', // Consider a different class if styling differs
            html: true // Ensure HTML content is allowed
          });
        }
      });
    },
    toggleWeather() {
      this.showWeather = !this.showWeather;
      console.log('Toggling weather:', this.showWeather);
    },
    onGpxClick() {
      const coords = this.getFeatureCollectionLineString();
      if (!coords) return;

      const gpx = geojsonToGpx({
        type: "Feature",
        properties: {}, // Add relevant properties if needed
        geometry: {
          type: "LineString",
          coordinates: coords,
        }
      }, this.timestamps,{
        creator: 'Hiiker', // Set creator metadata
        metadata: {
          name: this.name, // Add trail name to GPX metadata
          time: this.createdAt // Add creation time if available
        }
      });

      const blob = new Blob([gpx], { type: 'application/gpx+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.name || 'track'}.gpx`; // Use trail name for filename
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    onShareClick() {
      if(navigator.clipboard && this.shareableUrl) {
        navigator.clipboard.writeText(this.shareableUrl).then(() => {
          alert(this.$t('trail_data_card.share_url_copied'));
        }).catch(err => {
          console.error('Failed to copy URL: ', err);
          alert(this.$t('trail_data_card.could_not_copy_url'));
        });
      } else {
        alert(this.$t('trail_data_card.clipboard_not_available'));
      }
    },
    preview() {
      // Assuming these global functions exist and handle errors
      try {
        if (this.type === 'Recording' && window.showMapModalForRecordedTrail) {
          window.showMapModalForRecordedTrail(this.id);
        } else if (this.type === 'Ptv2::PlannedTrail' && window.showMapModalForPlannedTrail) {
          window.showMapModalForPlannedTrail(this.id);
        } else if ((this.type === 'Trail' || this.type === 'Hike') && window.showMapModalForTrail) {
          // showMapModalForTrail handles both Trail and Hike types (loads Hike data)
          window.showMapModalForTrail(this.id);
        } else {
          console.warn('Preview function not found for type:', this.type);
        }
      } catch (error) {
        console.error("Error calling preview function:", error);
        alert(this.$t('trail_data_card.could_not_open_preview'));
      }
    },
    crop() {
      if(this.shareableUrl) {
        window.location.href = `${this.shareableUrl}/crop`;
      }
    },
    sendToGarmin() {
      graph_client.request(SEND_TO_GARMIN, {klass: this.type, id: this.id}).then((response) => {
        if (response.sendToGarmin) {
          alert(this.$t('trail_data_card.sent_to_garmin'));
        } else {
          alert(this.$t('trail_data_card.failed_to_send_to_garmin'));
        }
      }).catch((error) => {
        console.error('Error sending trail to Garmin:', error);
        alert(this.$t('trail_data_card.error_sending_to_garmin'));
      });
    },
    async reportRecordingIssue() {
      if (!this.canReportRecordingIssue) {
        return;
      }

      const reportUrl = this.getRecordingReportIssueUrl();
      if (!reportUrl) {
        window.alert(this.$t('trail_data_card.unable_to_mark_inaccurate_now'));
        return;
      }

      try {
        const response = await fetch(reportUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content || '',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        const data = await response.json().catch(() => ({}));
        if (response.ok && data.success) {
          window.alert(this.$t('trail_data_card.recording_marked_inaccurate'));
        } else {
          window.alert(data.message || this.$t('trail_data_card.unable_to_mark_inaccurate_try_later'));
        }
      } catch (error) {
        console.error('Error reporting recording issue:', error);
        window.alert(this.$t('trail_data_card.unable_to_mark_inaccurate_try_later'));
      }
    },
    editInPlanner() {
      // Check if user is logged in using window.user
      if (!(window as any).user) {
        // User not logged in - trigger login
        if (window.showSignInModal) {
          window.showSignInModal();
        } else {
          alert(this.$t('trail_data_card.sign_in_to_edit'));
        }
        return;
      }

      // Navigate to planner with from_hike_id parameter (matches hike show page behavior)
      if (this.type === 'Hike' && this.obfuscatedSlug) {
        window.location.href = `/hike-planner?from_hike_id=${this.obfuscatedSlug}`;
      }
    },
    editDetails() {
      if(window.showEditRecordingModal) {
        try {
          // Use obfuscatedSlug instead of numeric ID for the edit modal
          const slugToUse = this.obfuscatedSlug || this.id;
          console.log('TrailDataCard editDetails - using slug:', slugToUse);
          window.showEditRecordingModal(slugToUse);
        } catch (error) {
          console.error("Error calling edit details modal:", error);
          alert(this.$t('trail_data_card.could_not_open_edit_details'));
        }
      } else {
        console.warn('showEditRecordingModal function not found');
      }
    },
    async onDeleteClick() { // Make async for potential await later
      const confirmClear = confirm(this.$t('trail_data_card.confirm_delete_route'));
      if (confirmClear) {
        this.isLoading = true; // Optional: Show loading state
        try {
          let deletePromise;
          if (this.type === 'Recording') {
            deletePromise = graph_client.request(DestroyRecording, { input: { id: this.id } });
          } else if (this.type === 'Ptv2::PlannedTrail') {
            deletePromise = graph_client.request(DestroyPtv2PlannedTrail, { plannedTrail: { id: this.id } });
          } else if (this.type === 'Trail') {
            console.warn('Deletion for type "Trail" is not implemented yet.');
            alert(this.$t('trail_data_card.deletion_not_supported'));
            this.isLoading = false;
            return; // Exit if type not supported
          } else {
            console.warn('Unknown type for deletion:', this.type);
            alert(this.$t('trail_data_card.cannot_delete_unknown_type'));
            this.isLoading = false;
            return; // Exit if type not supported
          }

          await deletePromise; // Wait for the GraphQL request
          alert(this.type === 'Recording'
            ? this.$t('trail_data_card.recording_deleted_reload')
            : this.$t('trail_data_card.plan_deleted_reload'));
          window.location.reload();

        } catch (err) {
          console.error(`Failed to delete ${this.type}:`, err);
          alert(this.type === 'Recording'
            ? this.$t('trail_data_card.failed_to_delete_recording')
            : this.$t('trail_data_card.failed_to_delete_plan'));
          this.isLoading = false; // Ensure loading state is turned off on error
        }
      }
    },
    getFeatureCollectionLineString() {
      return getFeatureCollectionLineString(this.featureCollection);
    },
    // Accessor handed to ElevationProfile so it drives the shared map marker
    // (createElevationMarker) itself on hover.
    getMapForMarker(): mapboxgl.Map | null {
      return (window as any).map ?? null;
    },
    formatDistance(distance: number) {
      const measurementPref = MeasurementPrefUtility.preference();
      return measurementPref.bigUnitFormatter(distance / 1000, true); // Use bigUnitFormatter for distance
    },
    formatElevation(elevation: number) {
      const measurementPref = MeasurementPrefUtility.preference();
      return measurementPref.smallUnitFormatter(elevation, false);
    },
    duration() {
      const prettyTime = new PrettyTime(this.durationSeconds).prettyTime();
      return prettyTime;
    },
    setStartPointAndEndPointFromFeatureCollection() {
      const coords = this.getFeatureCollectionLineString();
      if (coords && coords.length > 0) {
        this.startPoint = coords[0];
        this.endPoint = coords[coords.length - 1];
      } else {
        this.startPoint = null;
        this.endPoint = null;
      }
    },
    // ----------------------------------------------
//  put these two functions in <script lang="ts">
// ----------------------------------------------
    /* -------------------------------------------
 *  100 % drop-in for your existing component
 * -------------------------------------------*/
    async getWeather() {
      this.setStartPointAndEndPointFromFeatureCollection();
      if (!this.startPoint) { this.weather = null; this.addTooltip(); return; }

      const [lng, lat] = this.startPoint;
      const key        = import.meta.env.VITE_OPENWEATHER_KEY;

      const url = new URL('https://api.openweathermap.org/data/2.5/weather');
      url.search = new URLSearchParams({
        lat : lat.toString(),
        lon : lng.toString(),
        units : 'metric',
        appid : key,
      }).toString();

      try {
        const r = await fetch(url.toString());
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();                    //  ← the object you pasted
        this.weather = { start: this.fromOWM(json) };   //  ← adapter keeps UI intact
      } catch (e) {
        console.error('OWM fetch error', e);
        this.weather = null;
      } finally { this.addTooltip(); }
    },

    /**
     * Convert the OpenWeatherMap One-Call object so the rest of
     * the component does **not** need to change.
     */
    /**
     * Convert the `/weather` payload so the rest of
     * the code can stay exactly as it is.
     */
    fromOWM(w: any) {
      const day   = w.dt >= w.sys.sunrise && w.dt < w.sys.sunset ? 1 : 0;
      const rain  = w.rain?.['1h'] ?? 0;                         // may be undefined

      return {
        lat : w.coord.lat,
        lng : w.coord.lon,
        elevation : null,

        /* ------------ current ------------- */
        current : {
          temperature_2m : w.main.temp,
          weather_code   : w.weather?.[0]?.id,   //  numeric id (e.g. 800)
          icon           : w.weather?.[0]?.icon, //  "01d", "10n", …
          is_day         : day,
        },

        /* ------------ daily (array) ------------- */
        daily : {
          temperature_2m_max : [w.main.temp_max],
          temperature_2m_min : [w.main.temp_min],
          weather_code       : [w.weather?.[0]?.id],
          precipitation_sum  : [rain],
          sunrise            : [w.sys.sunrise * 1000],
          sunset             : [w.sys.sunset * 1000],
        },
      };
    },

    // --- WINDOW FUNCTION SETUP ---
    setupWindowFunctions() {
      window.trailCardDataHike = (hike) => {
        console.log("Loading Hike Data:", hike.id);
        this.id = hike.id;
        this.obfuscatedSlug = hike.obfuscatedSlug || '';
        this.ownerFirebaseUserId = hike.user?.firebaseUserId || null;
        this.name = hike.name || this.$t('trail_data_card.unnamed_hike');
        this.short_address = hike.startAddress?.cityCountry;
        this.featureCollection = JSON.parse(hike.geojsonRoutesOnlyString)
        this.elevationGainMeters = hike.elevationGainMeters || 0;
        this.durationSeconds = (hike.durationMinutes || 0) * 60; // Convert minutes to seconds
        this.mainImageUrl = hike.staticMapImageUrl || ''; // Provide fallback
        this.createdAt = hike.createdAt;
        this.shareableUrl = hike.shareableUrl;
        this.distanceMeters = hike.distanceMeters || 0;
        this.type = 'Hike';
        this.editUrl = null; // Always use click handler for hikes on explore map
        this.isDeletable = false; // Hikes usually not deletable by user this way
        this.isCropable = false;
        this.isDetailsEditable = false;
        this.difficulty = hike.difficulty || ''; // Use provided difficulty or empty string
        this.averageRating = hike.averageRating || ''; // Use provided rating or empty string
        this.hikeHighlights = hike.hikeHighlights || []; // Highlights for the elevation profile
        this.$emit('card-active', true); // Emit active state
      }
      window.trailCardDataRecording = (recording) => {
        console.log("Loading Recording Data:", recording.id);
        this.id = recording.id;
        this.obfuscatedSlug = recording.obfuscatedSlug || ''; // Store obfuscatedSlug
        this.name = recording.title || this.$t('trail_data_card.unnamed_recording');
        this.short_address = recording.startAddress?.city;
        this.featureCollection = typeof recording.lineString === 'string' ? JSON.parse(recording.lineString) : recording.lineString; // Handle parsed/unparsed
        this.elevationGainMeters = recording.elevationGainMeters || 0;
        this.durationSeconds = recording.elapsedSeconds || 0;
        this.mainImageUrl = recording.thumbnailUrl || ''; // Provide fallback
        this.createdAt = recording.createdAt;
        this.shareableUrl = recording.shareableUrl;
        this.distanceMeters = recording.distanceMeters || 0;
        this.type = 'Recording';
        this.ownerFirebaseUserId = recording.publicUser?.firebaseUserId
          || recording.publicUser?.firebase_user_id
          || recording.user?.firebaseUserId
          || recording.user?.firebase_user_id
          || null;
        this.editUrl = `/hike-planner?trail_type=Recording&trail_id=${recording.id}`;
        const isOwner = this.isOwnedByCurrentUser(this.ownerFirebaseUserId);
        this.isDeletable = isOwner;
        this.isCropable = isOwner;
        this.isDetailsEditable = isOwner;
        this.difficulty = ''; // Clear fields not applicable to Recording
        this.averageRating = '';
        this.hikeHighlights = []; // Recordings have no hike highlights
        this.$emit('card-active', true); // Emit active state
        this.timestamps = recording.timeStamps
      };

      window.trailCardDataPtv2PlannedTrail = (plannedTrail) => {
        console.log("Loading Planned Trail Data:", plannedTrail.id);
        this.id = plannedTrail.id;
        this.name = plannedTrail.title || this.$t('trail_data_card.unnamed_plan');
        this.type = 'Ptv2::PlannedTrail';
        this.featureCollection = plannedTrail.geojsonFeatureCollection; // Assumed already object
        this.elevationGainMeters = plannedTrail.elevationGainMeters || 0;
        this.durationSeconds = (plannedTrail.calculatedDurationMinutes || 0) * 60;
        this.mainImageUrl = plannedTrail.thumbnailUrl || '';
        this.createdAt = plannedTrail.createdAt;
        this.shareableUrl = plannedTrail.shareableUrl;
        this.distanceMeters = plannedTrail.distanceMeters || 0;
        this.editUrl = this.shareableUrl ? `${this.shareableUrl}/edit` : '';
        this.isDeletable = true;
        this.isCropable = false;
        this.isDetailsEditable = false;
        this.difficulty = ''; // Clear fields not applicable
        this.averageRating = '';
        this.hikeHighlights = []; // Planned trails have no hike highlights
        this.$emit('card-active', true);
      };

      (window as any).trailCardSetClusterNav = (active: boolean, index: number, total: number) => {
        this.clusterNavActive = active;
        this.clusterCurrentIndex = index;
        this.clusterTotalCount = total;
      };

      window.trailCardClearData = () => {
        // `floatingLayout` pages (recording_map) show one fixed recording —
        // there's nothing to "deselect". This global is also called by the
        // shared map-click handler (BaseMapDataOverlaysModule's
        // initMapClickHandler) whenever a click doesn't hit a queryable
        // vector-tile feature, which is the right behaviour for Explore.vue's
        // multi-trail map (click empty map = deselect the hovered/clicked
        // trail) but was ALSO firing here on every single map click, since a
        // recording's own route isn't among the toggleable overlay sources
        // that handler queries — wiping the whole card and elevation panel
        // just for tapping the map.
        if (this.floatingLayout) return;
        console.log("Clearing Trail Card Data");
        this.id = '';
        this.name = '';
        this.type = '';
        this.featureCollection = null;
        this.weather = null;
        this.isCropable = false;
        this.isDeletable = false;
        this.isDetailsEditable = false;
        this.ownerFirebaseUserId = null;
        this.mainImageUrl = '';
        this.shareableUrl = '';
        this.editUrl = null;
        this.difficulty = '';
        this.averageRating = '';
        this.showWeather = false; // Hide weather panel
        this.mobileDetailsOpen = false;
        this.mobileElevationOpen = false;
        this.clusterNavActive = false;
        this.clusterCurrentIndex = 0;
        this.clusterTotalCount = 0;
        this.hikeHighlights = [];
        // The ElevationProfile tears down its own Chart.js instance and shared
        // map marker on unmount (the card is removed via v-if="hasData").
        this.$emit('card-active', false); // Card becomes inactive
      };

      window.trailCardDataTrail = (trail) => {
        console.log("Loading Trail Data:", trail.id);
        this.id = trail.id;
        this.name = trail.name || this.$t('trail_data_card.unnamed_trail');
        this.difficulty = trail.difficulty;
        this.averageRating = trail.averageRating;
        this.type = 'Trail';
        this.featureCollection = typeof trail.lineString?.geoData === 'string' ? JSON.parse(trail.lineString.geoData) : trail.lineString?.geoData; // Handle parsed/unparsed
        this.durationSeconds = (trail.durationMinutes || 0) * 60;
        this.elevationGainMeters = trail.totalElevation || 0; // Use totalElevation field
        this.distanceMeters = trail.trailDistance || 0; // Use trailDistance field

        if (trail.photos && trail.photos.length > 0 && trail.photos[0].url) {
          this.mainImageUrl = trail.photos[0].url;
        } else {
          this.mainImageUrl = trail.mobileStaticMapImage || ''; // Fallback
        }
        this.createdAt = trail.createdAt;
        const origin = window.location.origin;
        // Ensure slugs exist before constructing URL
        this.shareableUrl = (trail.countrySlug && trail.regionSlug && trail.slug)
          ? `${origin}/trails/${trail.countrySlug}/${trail.regionSlug}/${trail.slug}`
          : '';
        this.editUrl = `/hike-planner?trail_type=Trail&trail_id=${trail.id}`;
        this.isDeletable = false; // Trails usually not deletable by user this way
        this.isCropable = false;
        this.isDetailsEditable = false;
        this.hikeHighlights = []; // Curated trails have no hike highlights
        this.$emit('card-active', true); // Card becomes active
      };
    }
  },
  created() {
    // Expose window.* methods for loading data into this card
    this.setupWindowFunctions();
  },
  mounted() {
    this.addTooltip(); // Initialize tooltips on mount
  },
  beforeUnmount() {
    // The ElevationProfile component owns its Chart.js instance and shared map
    // marker and cleans them up on its own unmount.
    // Clean up tooltips
    if (this.weatherTooltip) {
      this.weatherTooltip.dispose();
    }
    if (this.optionsTooltip) {
      this.optionsTooltip.dispose();
    }
    // Optional: Clean up global functions if appropriate for your app structure
    // delete window.trailCardDataRecording;
    // delete window.trailCardDataPtv2PlannedTrail;
    // delete window.trailCardClearData;
    // delete window.trailCardDataTrail;
  },
});

// ----- DECLARE GLOBAL WINDOW FUNCTIONS (TypeScript) -----
// This tells TypeScript that these functions might exist on the window object
declare global {
  interface Window {
    trailCardDataRecording?: (recording: any) => void;
    trailCardDataHike?: (hike: any) => void;
    trailCardDataPtv2PlannedTrail?: (plannedTrail: any) => void;
    trailCardClearData?: () => void;
    trailCardSetClusterNav?: (active: boolean, index: number, total: number) => void;
    trailCardDataTrail?: (trail: any) => void;
    clusterNavigate?: (direction: 'next' | 'prev') => void;
    clearClusterNav?: () => void;
    user?: any;
    admin?: boolean | string;
    showMapModalForRecordedTrail?: (id: string, shareConfig?: Record<string, string> | null) => void;
    showMapModalForPlannedTrail?: (id: string) => void;
    showMapModalForTrail?: (id: string) => void;
    showEditRecordingModal?: (id: string) => void;
    map?: mapboxgl.Map; // Assuming map instance might be on window
  }
}
</script>

<style scoped>
/* Cluster Navigation Bar */
.cluster-nav {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(23, 24, 29, 0.9);
  border: 1px solid rgba(172, 176, 191, 0.3);
  border-radius: 20px;
  padding: 4px 12px;
  z-index: 10;
  white-space: nowrap;
}

.cluster-nav-btn {
  background: none;
  border: none;
  color: #ccc;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: color 0.2s ease, background-color 0.2s ease;
}

.cluster-nav-btn:hover {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.1);
}

.cluster-nav-btn .material-icons {
  font-size: 20px;
}

.cluster-nav-label {
  color: #fff;
  font-size: 0.85rem;
  font-weight: 500;
  user-select: none;
}

/* Main trail card container - Ensure position: relative on PARENT */
.trail-data-card {
  /* --- CORE POSITIONING RULES --- */
  /* These position the card relative to its nearest positioned ancestor */
  position: absolute;   /* Relative to nearest positioned ancestor */
  bottom: 1dvh;        /* Adjusted bottom position */
  left: 0;            /* Align left edge to ancestor's left */
  right: 0;           /* Align right edge to ancestor's right */
  margin: 1em auto;   /* Centers horizontally when left/right=0 */
  width: 80%;         /* Percentage width of the ancestor */
  max-width: 960px;   /* Max pixel width */
  z-index: 1000;      /* Stacking order */
  /* --- END CORE POSITIONING --- */

  display: flex;
  flex-direction: row;
  border-radius: 10px;
  background: rgba(23, 24, 29, 0.85);
  border: 1px solid rgba(172, 176, 191, 0.3); /* Softer border */
  color: #ffffff; /* Ensure text is visible */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3); /* Add shadow */
}

.trail-data-card.reduce {
  max-width: 510px;
}

/* == Top Right Controls Styling == */
.card-controls {
  position: absolute; /* Position this container */
  top: 8px;
  right: 8px;
  z-index: 10; /* Above other content */
  display: flex; /* Arrange buttons horizontally */
  align-items: center; /* Align items vertically */
  gap: 4px; /* Add space between the buttons */
}

/* Style for buttons INSIDE the container */
.card-controls .close-button,
.card-controls .hide-show {
  /* position: absolute; REMOVED - positioned by flex container */
  font-size: 22px; /* Consistent size */
  cursor: pointer;
  padding: 5px; /* Click area */
  color: #ccc;
  transition: color 0.2s ease, background-color 0.2s ease;
  border-radius: 50%; /* Optional: circle background */
  background-color: rgba(0, 0, 0, 0.1); /* Optional: slight bg */
  line-height: 1; /* Prevent extra space */
  display: flex; /* Ensure icon centers within padding */
  align-items: center;
  justify-content: center;
}

.card-controls .close-button:hover,
.card-controls .hide-show:hover {
  color: #fff;
  background-color: rgba(0, 0, 0, 0.4); /* Darker bg on hover */
}

/* Adjust positioning when reduced (container is already top-right) */
.trail-data-card.reduce .card-controls {
  /* Usually okay, but adjust `right` if layout shifts significantly */
  /* right: 15px; */ /* Example if padding/margins change */
}


/* Dropdown Menu */
.dropdown-menu{
  overflow: hidden;
  background-color: rgba(40, 42, 50, 0.95);
  border: 1px solid rgba(172, 176, 191, 0.3);
  padding: 0.25rem 0;
  min-width: auto; /* Allow dropdown to be smaller */
}

.dropdown-item{
  cursor: pointer;
  color: #ccc;
  padding: 0.35rem 1rem;
  font-size: 0.8rem;
  transition: background-color 0.2s ease, color 0.2s ease;
  white-space: nowrap; /* Prevent wrapping */
}
.dropdown-item:hover, .dropdown-item:focus {
  background-color: rgba(70, 73, 85, 0.9);
  color: #fff;
}
.dropdown-item a {
  color: inherit;
  text-decoration: none;
  display: block;
}


/* Left Panel */
.left-panel {
  flex: 0 0 50%;
  border-right: 1px solid rgba(172, 176, 191, 0.3);
  display: flex;
  flex-direction: row;
  align-items: stretch;
  min-height: 110px; /* Minimum height */
}

.trail-data-card.reduce .left-panel {
  flex: 1; /* Take full width when reduced */
  border-right: none; /* No border when reduced */
}

/* Right Panel: Elevation Profile */
.right-panel {
  flex: 1; /* Allow right panel to grow */
  padding: 10px 15px;
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: center;
}

/* Embed the shared ElevationProfile compactly inside the card's right panel:
   fill the available height, drop its standalone chrome (own header/background),
   and let the card container provide the surface. */
.right-panel :deep(.elevation-profile) {
  flex: 1;
  min-width: 0;
  height: 100%;
  background: transparent;
  box-shadow: none;
  border-radius: 0;
}

.right-panel :deep(.profile-header) {
  display: none;
}

.right-panel :deep(.profile-content) {
  padding: 0;
}

.trail-data-card.reduce .right-panel {
  display: none;
}

/* Thumbnail container */
.thumbnail-container {
  width: 110px;
  flex-shrink: 0;
  height: 100%; /* Fill height of left panel */
  overflow: hidden;
  display: flex;
  align-items: center; /* Added for vertical centering if image aspect ratio differs */
  justify-content: center; /* Added for horizontal centering */
  border-top-left-radius: 9px; /* Match parent border radius - 1px */
  border-bottom-left-radius: 9px;
}

.thumbnail-container img {
  width: 100%;
  height: 100%; /* Fill container */
  object-fit: cover; /* Cover maintains aspect ratio */
  display: block; /* Remove potential extra space */
}

.left-panel p, .left-panel a {
  margin-bottom: 0.25rem;
  line-height: 1.3;
}
.left-panel strong {
  color: #fff;
  font-size: 1.1em;
  display: block; /* Ensure proper spacing */
  margin-bottom: 0.1rem;
}
.left-panel a {
  color: #eee;
  text-decoration: none !important;
  transition: color 0.2s ease;
}
.left-panel a:hover {
  color: #fff;
  text-decoration: underline !important;
}
.text-muted {
  color: #aaa !important;
}

/* Badges */
.badge {
  background-color: rgba(60, 63, 73, 0.8);
  border: none;
  font-weight: 500;
  vertical-align: middle; /* Align badges inline */
  padding: 0.3em 0.6em !important; /* Adjust padding */
  font-size: 0.8em; /* Slightly smaller badges */
}
.badge .material-icons {
  font-size: 1.1em;
  vertical-align: text-bottom; /* Align icon better with text */
  margin-right: 2px; /* Space between icon and text */
}
/* Ensure badges wrap correctly */
.left-panel .d-flex.flex-row {
  flex-wrap: wrap;
  gap: 5px; /* Add gap between wrapped items */
}


/* Elevation chart container */
.elevation-profile-placeholder {
  width: 100%;
  height: 100%;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-container {
  width: 100%;
  height: 100%;
  max-height: 100px;
  position: relative;
}

.chart-container canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

/* Card transition */
.trail-card-slide-enter-active,
.trail-card-slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 0.4s ease;
}

.trail-card-slide-enter-from,
.trail-card-slide-leave-to {
  transform: translateY(110%);
  opacity: 0;
}

.trail-card-slide-leave-from,
.trail-card-slide-enter-to {
  transform: translateY(0);
  opacity: 1;
}


/* Responsive Adjustments */
@media screen and (max-width: 992px) {
  .trail-data-card {
    width: 80%; /* Wider on medium screens */
  }
  .left-panel {
    width: 50%;
    flex: 1 1 auto;
    border-right: none;
    border-bottom: 1px solid rgba(172, 176, 191, 0.2);
    border-bottom-left-radius: 0; /* Reset radius */
  }
  .right-panel {
    width: 50%;
    flex: 1 1 auto;
    min-height: 100px;
    padding: 10px;
    border-bottom-left-radius: 9px; /* Apply radius */
    border-bottom-right-radius: 9px;
  }
  .trail-data-card.reduce .left-panel {
    border-bottom: none; /* No bottom border when reduced */
    border-bottom-left-radius: 9px; /* Apply radius */
    border-bottom-right-radius: 9px;
  }
  .trail-data-card.reduce .right-panel {
    display: none; /* Keep hiding right panel when reduced */
  }
  .thumbnail-container {
    width: 100px; /* Adjust thumbnail */
    height: 100px; /* Fixed height might work better */
    border-bottom-left-radius: 0; /* Reset */
    border-top-right-radius: 0; /* Reset when stacked */
  }
}


@media screen and (max-width: 768px) {
  .trail-data-card {
    width: 70%;   /* Adjusted width */
    left: 2.5%;   /* Use left/right for centering */
    right: 2.5%;
    margin-left: auto; /* Reset margin auto */
    margin-right: auto;
    /* flex-direction: column; Inherited */
  }
  /* Keep column layout */
  .right-panel:not(.force-show) { /* Hide graph panel by default */
    display: none;
  }
  .left-panel {
    border-bottom: none; /* No border needed if right panel is hidden */
    border-bottom-left-radius: 9px; /* Apply radius */
    border-bottom-right-radius: 9px;
  }
  .thumbnail-container {
    width: 80px;
    height: 80px;
  }
  .left-panel .px-2 {
    padding: 8px 12px !important;
  }
  .left-panel strong {
    font-size: 1em;
  }
  .left-panel p {
    font-size: 0.9em;
  }
  .badge {
    font-size: 0.75em;
    padding: 0.25rem 0.4rem !important;
  }
  .badge .material-icons {
    font-size: 1em;
  }
  /* Adjust controls position slightly */
  .card-controls {
    top: 5px;
    right: 5px;
  }
  .card-controls .close-button,
  .card-controls .hide-show {
    font-size: 20px;
    padding: 4px;
  }

  /* Hide all tooltips on mobile screens */
  :global(.tooltip) {
    display: none !important;
  }
}

@media screen and (max-width: 480px) {
  .trail-data-card {
    width: 95%;
  }
  .left-panel {
    flex-direction: column; /* Stack thumbnail on top of text */
  }
  .thumbnail-container {
    display: none !important;
  }
  .left-panel .px-2 {
    padding: 12px !important;
  }
  .badge {
    font-size: 0.7em;
  }
  .hide-show{
    display: none !important;
  }
}

/* Tooltip Custom Class */
:global(.tooltip.weather-tooltip .tooltip-inner) {
  background-color: rgba(255, 255, 255, 0.95);
  color: #333;
  font-size: 0.85rem;
  padding: 6px 10px;
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
}
:global(.tooltip.weather-tooltip .tooltip-arrow::before) {
  border-top-color: rgba(255, 255, 255, 0.95);
}

/* Bootstrap 5 Tooltip Arrow Color Overrides */
:global(.bs-tooltip-auto[data-popper-placement^=top] .tooltip-arrow::before),
:global(.bs-tooltip-top .tooltip-arrow::before){
  border-top-color: rgba(255, 255, 255, 0.95) !important;
}
:global(.bs-tooltip-auto[data-popper-placement^=bottom] .tooltip-arrow::before),
:global(.bs-tooltip-bottom .tooltip-arrow::before){
  border-bottom-color: rgba(255, 255, 255, 0.95) !important;
}
:global(.bs-tooltip-auto[data-popper-placement^=left] .tooltip-arrow::before),
:global(.bs-tooltip-left .tooltip-arrow::before){
  border-left-color: rgba(255, 255, 255, 0.95) !important;
}
:global(.bs-tooltip-auto[data-popper-placement^=right] .tooltip-arrow::before),
:global(.bs-tooltip-right .tooltip-arrow::before){
  border-right-color: rgba(255, 255, 255, 0.95) !important;
}

/* ============================================================
   Floating layout variant (recording map): a left-docked sidebar,
   consistent with the hike map's flush trail panel, plus an
   independent floating elevation bar. Purpose-built markup/styles —
   does not share DOM with the default joined layout above, so
   plan_map/explore_v2 are untouched.
   ============================================================ */
.recording-sidebar {
  --sidebar-width: 320px;
  position: absolute;
  /* Unlike the hike map (which has a breadcrumb bar above the map giving it
     natural breathing room), this page's map sits directly under the site
     nav, so the sidebar needs its own gap on both edges to avoid looking
     flush against the nav bar / cut off at the browser edge. */
  top: 16px;
  left: 16px;
  z-index: 1000;
}

.sidebar-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(23, 24, 29, 0.9);
  border: 1px solid rgba(172, 176, 191, 0.3);
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  color: #fff;
  cursor: pointer;
}

.sidebar-tab:hover {
  background: rgba(40, 42, 50, 0.95);
}

.sidebar-tab .material-icons {
  font-size: 22px;
  color: rgba(255, 255, 255, 0.85);
}

.sidebar-panel {
  width: var(--sidebar-width);
  max-width: calc(100vw - 32px);
  background: rgba(23, 24, 29, 0.9);
  border: 1px solid rgba(172, 176, 191, 0.3);
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  color: #fff;
  position: relative;
}

.sidebar-header {
  display: flex;
  align-items: center;
  gap: 4px;
  /* Left padding matches .sidebar-body/.sidebar-footer's 14px so the icon
     lines up with the address/total text below it instead of sitting
     further right than everything under it. */
  padding: 10px 8px 10px 14px;
  border-bottom: 1px solid rgba(172, 176, 191, 0.2);
}

.sidebar-title {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-icon-btn {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.sidebar-icon-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.sidebar-icon-btn .material-icons {
  font-size: 20px;
}

.sidebar-more {
  position: relative;
}

.sidebar-body {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 14px;
}

.sidebar-address {
  margin: 0;
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.65);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-weather-chip {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px 2px 4px;
  background: rgba(255, 255, 255, 0.08);
  border: none;
  border-radius: 999px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.sidebar-weather-chip img {
  width: 20px;
  height: 20px;
}

.sidebar-rating {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.sidebar-rating .material-icons {
  font-size: 14px;
  color: #ffdf46;
}

.sidebar-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-top: 1px solid rgba(172, 176, 191, 0.2);
  border-radius: 0 0 10px 10px;
}

.sidebar-footer-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.sidebar-footer-value {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
}

/* Anchor the weather popup to the right of the sidebar instead of above it —
   the sidebar sits flush against the top of the map, so "above" would go
   off-screen. */
.sidebar-panel :deep(.weather-toast) {
  position: absolute;
  left: 100%;
  right: auto;
  top: 0;
  bottom: auto;
  margin-left: 12px;
  width: 300px;
}

.floating-elevation-bar {
  position: absolute;
  bottom: 16px;
  /* Centered within the map area to the right of the sidebar, not the
     whole viewport, so it doesn't skew toward the sidebar's side. */
  left: calc(50% + var(--sidebar-width, 320px) / 2);
  transform: translateX(-50%);
  width: min(90vw, 900px);
  z-index: 1000;
}

.mobile-sheet-close {
  display: none;
}

.recording-mobile-tabs {
  display: none;
}

/* Below the desktop breakpoint, showing the sidebar and the elevation bar at
   once leaves no room for the map. Instead, hide both by default behind a
   bottom tab bar (matching the hike map's Routes/Elevation pattern) and only
   show one at a time, full-width, as a bottom sheet when picked. */
@media screen and (max-width: 768px) {
  .recording-sidebar {
    display: none;
  }

  .recording-sidebar.mobile-open {
    /* The map area is only 90svh, so an absolute bottom:0 here would dock to
       90% of the viewport instead of the real screen edge — use fixed to
       anchor to the true bottom like a native bottom sheet. */
    position: fixed;
    display: block;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
  }

  /* elevation-panel-v2's own bottom sheet is always at least peeked (a fixed
     88px strip) behind/below this card, also anchored to the true viewport
     bottom — without this offset the two fixed-position sheets stack right
     on top of each other, hiding this card's weather/total rows underneath
     the peeked elevation panel instead of sitting above it. Matches
     `.recording-mobile-tabs.above-elevation-panel-v2`'s identical offset. */
  .recording-sidebar.mobile-open.above-elevation-panel-v2 {
    bottom: calc(88px + var(--safe-area-bottom, 0px));
  }

  .recording-sidebar.mobile-open .sidebar-panel {
    width: 100%;
    max-width: none;
    border-left: 1px solid rgba(172, 176, 191, 0.3);
    border-bottom: none;
    border-radius: 16px 16px 0 0;
    max-height: 55vh;
    overflow-y: auto;
  }

  .sidebar-panel :deep(.weather-toast) {
    left: 0;
    right: 0;
    top: calc(100% + 12px);
    margin-left: 0;
    width: auto;
  }

  .floating-elevation-bar {
    display: none;
  }

  /* elevation-panel-v2 manages its own peek/default/expanded bottom-sheet
     states internally (see BottomSheet.vue) — unlike the fallback panel, it
     isn't gated behind the Elevation tab/mobileElevationOpen, so it must stay
     visible rather than inheriting the display:none default above.
     `display: flex` (not `block`) — BottomSheet.vue's own `.bottom-sheet`
     rule needs `display: flex` to stack its handle/body and to let
     `.bottom-sheet-body { flex: 1 }` actually fill the sheet's height (e.g.
     the 'expanded' snap's chart-fills-available-space behaviour). Both rules
     target this same element with equal specificity (one scoped class each),
     so whichever loses the cascade order silently breaks BottomSheet's
     layout — matching the display here removes that footgun regardless of
     load order. */
  .floating-elevation-bar--v2 {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    transform: none;
  }

  .floating-elevation-bar.mobile-open {
    display: block;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    transform: none;
  }

  .floating-elevation-bar.mobile-open :deep(.elevation-profile) {
    border-radius: 16px 16px 0 0;
    height: 280px;
  }

  /* Reserve space so the stats row doesn't run under the close button that
     replaces ElevationProfile's own (disabled) expand button in this corner. */
  .floating-elevation-bar.mobile-open :deep(.profile-header) {
    padding-right: 44px;
  }

  .floating-elevation-bar.mobile-open .mobile-sheet-close {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 10px;
    right: 10px;
    width: 30px;
    height: 30px;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    cursor: pointer;
    z-index: 5;
  }

  .floating-elevation-bar.mobile-open .mobile-sheet-close:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  /* The map's zoom/style/options controls stack across two rows and sit
     top-center on narrow screens — the sidebar no longer sits up there
     (it's a bottom sheet now), so nothing to avoid colliding with. Fixed
     (not absolute) so it anchors to the real viewport bottom rather than
     the map area's 90svh bound. */
  /* One joined pill (like the hike map's "Routes | Elevation" bar), not two
     separate floating buttons. */
  .recording-mobile-tabs {
    display: flex;
    position: fixed;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1000;
    background: rgba(23, 24, 29, 0.9);
    border: 1px solid rgba(172, 176, 191, 0.3);
    border-radius: 999px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    overflow: hidden;
  }

  .recording-mobile-tabs.hidden {
    display: none;
  }

  /* elevation-panel-v2's peek snap point is a fixed 88px strip (see its
     `snapPoints` in ElevationPanelV2.vue) that shares this same fixed
     bottom-of-viewport stacking context — without this offset the tab bar
     renders on top of the peeked sheet's header text. */
  .recording-mobile-tabs.above-elevation-panel-v2 {
    bottom: calc(88px + 16px + var(--safe-area-bottom, 0px));
  }

  .mobile-tab-divider {
    width: 1px;
    align-self: stretch;
    margin: 8px 0;
    background: rgba(172, 176, 191, 0.3);
  }

  .mobile-tab-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 18px;
    background: transparent;
    border: none;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }

  .mobile-tab-btn:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  .mobile-tab-btn .material-icons {
    font-size: 18px;
    color: #ffdf46;
  }
}

/* Elevation Marker Styling */
:global(.elevation-marker) { /* Use :global for unscoped CSS */
  display: block;
  border: 2px solid #fff;
  background-color: #e74c3c; /* Example color: reddish */
  border-radius: 50%;
  width: 12px; /* Marker size */
  height: 12px;
  box-shadow: 0 0 5px rgba(0,0,0,0.5);
  cursor: pointer;
  /* Ensure anchor is correct if using custom element */
  transform: translate(-50%, -100%); /* Centers horizontally, anchors bottom */
}
</style>
